import React, { useEffect, useState } from 'react';
import saasAdminApi from '../../services/saasAdminApi';
import { formatApiError } from '../../utils/errorMessage';

// The add-ons the chosen plan can take (from the server's plan list).
export function addonsForPlan(options, planKey) {
  const o = (options || []).find((p) => p.key === planKey);
  return o ? o.addons || [] : [];
}

// Request body for exempting a location. The add-on box only counts when the plan sells one.
export function exemptPayload(options, planKey, includeAddon, note) {
  return {
    plan: planKey,
    include_bank_feeds_addon: Boolean(includeAddon) && addonsForPlan(options, planKey).length > 0,
    note: note ? String(note) : null
  };
}

export function ExemptBadge({ row }) {
  if (!row || !row.billing_exempt) return null;
  return (
    <span data-testid="exempt-badge" className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
      Exempt{row.exempt_bank_feeds_addon ? ' + bank feeds' : ''}
    </span>
  );
}

function ExemptEditor({ row, options, onSaved }) {
  const [plan, setPlan] = useState(row.exempt_plan || (options[options.length - 1] || {}).key || '');
  const [addon, setAddon] = useState(Boolean(row.exempt_bank_feeds_addon));
  const [note, setNote] = useState(row.exempt_note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const addons = addonsForPlan(options, plan);

  const save = async () => {
    setBusy(true); setErr('');
    try { await saasAdminApi.setBillingExempt(row.business_id, exemptPayload(options, plan, addon, note)); onSaved(); }
    catch (e) { setErr(formatApiError(e, 'Could not save the exemption')); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setErr('');
    try { await saasAdminApi.clearBillingExempt(row.business_id); onSaved(); }
    catch (e) { setErr(formatApiError(e, 'Could not remove the exemption')); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select aria-label="Exempt plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="border rounded px-2 py-1 text-sm">
        {options.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
      </select>
      {addons.length > 0 && (
        <label className="text-sm flex items-center gap-1">
          <input type="checkbox" aria-label="Include bank feeds add-on" checked={addon} onChange={(e) => setAddon(e.target.checked)} />
          Include {addons[0].name} free
        </label>
      )}
      <input aria-label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="border rounded px-2 py-1 text-sm" />
      <button type="button" disabled={busy || !plan} onClick={save} className="px-3 py-1 text-sm rounded bg-purple-600 text-white">
        {row.billing_exempt ? 'Update exemption' : 'Exempt from billing'}
      </button>
      {row.billing_exempt && (
        <button type="button" disabled={busy} onClick={remove} className="px-3 py-1 text-sm rounded border">Remove exemption</button>
      )}
      {err && <span className="text-sm text-red-600">{err}</span>}
    </div>
  );
}

export default function BillingExemptions() {
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [b, p] = await Promise.all([saasAdminApi.listBusinesses(), saasAdminApi.getExemptPlans()]);
      setRows(b.data || []); setOptions(p.data || []); setError('');
    } catch (e) { setError(formatApiError(e, 'Failed to load locations')); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold">Locations and billing exemptions</h3>
        <p className="text-sm text-gray-500">An exempt location keeps the chosen plan with no charge and no end date.</p>
      </div>
      {error && <div className="px-6 py-2 text-sm text-red-600">{error}</div>}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exemption</th>
        </tr></thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((r) => (
            <tr key={r.business_id}>
              <td className="px-6 py-3 text-sm"><div className="font-medium">{r.business_name}</div><div className="text-gray-500">{r.business_owner_email}</div></td>
              <td className="px-6 py-3 text-sm capitalize">{r.plan_name || 'none'} {r.status ? `(${r.status})` : ''}<ExemptBadge row={r} /></td>
              <td className="px-6 py-3"><ExemptEditor row={r} options={options} onSaved={load} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
